import { type FC, useEffect, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { useLocation } from "react-router";
import { useComposer, useComposerRuntime, useThread, useThreadRuntime } from "@assistant-ui/react";
import { useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { AssistantProvider, ChatPersistenceProvider, useChatPersistence } from "@acto/taskpane/core/chat";
import type { CustomerContext, NavigationState } from "@acto/taskpane/core/chat";
import { TableToolUI } from "@acto/taskpane/core/components/chat/TableToolUI";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import { useBackNavigation } from "@acto/taskpane/field-sales-2/navigation";
import { usePageEnterAnimation } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { ChatHeader } from "./ChatHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInputDock } from "./ChatInputDock";
import { ChatHistoryView } from "./history/ChatHistoryView";
import { useChatSession } from "./useChatSession";

// Fills the field-sales-2 Layout; the message viewport scrolls inside while the input dock is fixed.
const Page = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  width: "100%",
  overflow: "hidden",
});

interface ChatPageContentProps {
  customerContext: CustomerContext | null;
  initialMessage?: string;
  onNewChat: (message?: string) => void;
}

function ChatPageContent({ customerContext, initialMessage, onNewChat }: ChatPageContentProps) {
  const tChat = useTranslations("chat");
  const handleBack = useBackNavigation(ActoRoutes.HOME);
  const thread = useThread();
  const composer = useComposer();
  const composerRuntime = useComposerRuntime();
  const threadRuntime = useThreadRuntime();
  const hasProcessedInitialMessage = useRef(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const track = useTrack();

  usePageEnterAnimation(pageRef);

  useViewTrackedOnce(() =>
    track(AnalyticsEvents.CHAT_OPENED, {
      source: customerContext ? "customer_details" : "chat_page",
      customerId: customerContext?.id,
      customerName: customerContext?.name,
    }),
  );

  const threadState = threadRuntime.getState();
  const sessionId = threadState?.threadId || "";

  const { chatHistoryId } = useChatPersistence({
    customerId: customerContext?.id,
    sessionId,
  });

  useEffect(() => {
    if (hasProcessedInitialMessage.current) return;
    if (initialMessage) {
      hasProcessedInitialMessage.current = true;
      composerRuntime.setText(initialMessage);
      composerRuntime.send();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = () => {
    const messageText = composer.text.trim();
    track(AnalyticsEvents.CHAT_MESSAGE_SENT, {
      hasCustomerContext: Boolean(customerContext),
      customerId: customerContext?.id,
      messageLength: messageText.length,
      message: messageText,
    });
    composerRuntime.send();
  };

  return (
    <ChatPersistenceProvider chatHistoryId={chatHistoryId} customerId={customerContext?.id}>
      <Page ref={pageRef}>
        <ChatMessageList
          header={
            <ChatHeader
              customer={customerContext}
              onBack={handleBack}
              // History is per-customer: a chat opened without one is never persisted, so there is
              // nothing to list. Starting a fresh chat works either way.
              onOpenHistory={customerContext ? () => setHistoryOpen(true) : undefined}
              onNewChat={() => onNewChat()}
            />
          }
        />
        <ChatInputDock
          value={composer.text}
          placeholder={tChat("chatPlaceholder")}
          loading={thread.isRunning}
          onChange={(value) => composerRuntime.setText(value)}
          onSend={handleSend}
          onAbort={() => threadRuntime.cancelRun()}
        />
        {customerContext && historyOpen && (
          <ChatHistoryView
            customer={customerContext}
            onClose={() => setHistoryOpen(false)}
            onStartNewChat={(message) => {
              setHistoryOpen(false);
              onNewChat(message);
            }}
          />
        )}
      </Page>
    </ChatPersistenceProvider>
  );
}

export const ChatPage: FC = () => {
  const location = useLocation();
  const customerContext = (location.state as NavigationState | null)?.customerContext;

  const { session, startNewChat } = useChatSession(customerContext?.id);

  return (
    <AssistantProvider key={session.key} customerContext={customerContext ?? null}>
      <TableToolUI />
      <ChatPageContent
        customerContext={customerContext ?? null}
        initialMessage={session.initialMessage}
        onNewChat={startNewChat}
      />
    </AssistantProvider>
  );
};
